'use client';

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProjectRecord } from '@/lib/projects/project-types';
import {
  getSectorCounts,
  getCountryCountsTop,
  getProjectsOverTime,
} from '@/lib/dashboard/dashboard-utils';

interface DashboardChartsProps {
  projects: ProjectRecord[];
}

/* Shared styled tooltip matching shadcn card surface */
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 text-sm shadow-md">
      <p className="font-medium mb-0.5">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-muted-foreground">
          {entry.name}: <span className="font-semibold text-foreground">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

const AXIS_TICK = { fontSize: 11, fill: 'var(--muted-foreground)' };
const GRID_PROPS = { strokeDasharray: '3 3', stroke: 'var(--border)' };

export const DashboardCharts = React.memo(function DashboardCharts({ projects }: DashboardChartsProps) {
  const sectorData = useMemo(() => {
    const all = getSectorCounts(projects);
    if (all.length <= 8) return all;
    const top = all.slice(0, 8);
    const rest = all.slice(8).reduce((sum, d) => sum + d.value, 0);
    return [...top, { name: 'Other', value: rest }];
  }, [projects]);

  const countryData = useMemo(
    () => getCountryCountsTop(projects, 8),
    [projects]
  );

  const timeData = useMemo(
    () => getProjectsOverTime(projects, 'weekly'),
    [projects]
  );

  const timeDataHasValues = timeData.some((d) => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Projects by Sector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Projects by Sector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sectorData}
                margin={{ top: 5, right: 20, left: 0, bottom: 40 }}
              >
                <CartesianGrid {...GRID_PROPS} />
                <XAxis
                  dataKey="name"
                  tick={AXIS_TICK}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={60}
                />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Projects" fill="var(--chart-3)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Projects by Country */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Projects by Country (Top 8)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={countryData}
                margin={{ top: 5, right: 20, left: 0, bottom: 40 }}
              >
                <CartesianGrid {...GRID_PROPS} />
                <XAxis
                  dataKey="name"
                  tick={AXIS_TICK}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={60}
                />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" name="Projects" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Projects over time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Projects Over Time (Last 12 Weeks)</CardTitle>
        </CardHeader>
        <CardContent>
          {timeDataHasValues ? (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={timeData}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="name" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Projects"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    dot={{ r: 3, fill: 'var(--chart-1)' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-56 flex items-center justify-center">
              <p className="text-sm text-muted-foreground">No activity in the selected period</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});
